import { Link, useForm } from "@inertiajs/react";
import { Mail } from "lucide-react";
import {
  bizminer_app_password_resets_path,
  new_bizminer_app_session_path,
} from "@/_generated/routes/routes";
import type BizminerOrganizationDisplay from "@/_generated/types/serializers/BizminerOrganizationDisplay";
import { AuthCard } from "@/components/auth/AuthCard.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Label } from "@/components/ui/label.tsx";

interface SuccessMessageProps {
  email?: string;
}

const SuccessMessage = ({ email }: SuccessMessageProps) => (
  <div className="flex flex-col items-center text-center">
    <div className="bg-primary/10 mb-4 flex h-16 w-16 items-center justify-center rounded-full">
      <Mail className="text-primary h-8 w-8" />
    </div>
    <p className="text-muted-foreground text-balance">
      If an account exists for <strong>{email}</strong>, we sent a password
      reset link.
    </p>
  </div>
);

interface ResetFormProps {
  onSubmit: (e: React.FormEvent) => void;
  email: string;
  onEmailChange: (value: string) => void;
  processing: boolean;
}

const ResetForm = ({
  onSubmit,
  email,
  onEmailChange,
  processing,
}: ResetFormProps) => (
  <form onSubmit={onSubmit}>
    <div className="flex flex-col gap-4">
      <div className="grid gap-2">
        <Label htmlFor="email_address">Email address</Label>
        <Input
          id="email_address"
          name="email_address"
          type="email"
          autoComplete="email"
          placeholder="you@company.com"
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
          required
        />
      </div>
      <Button type="submit" className="w-full mt-2" disabled={processing}>
        {processing ? "Sending..." : "Send reset link"}
      </Button>
    </div>
  </form>
);

interface Props {
  submitted?: boolean;
  email?: string;
  organization_slug: string;
  organization?: BizminerOrganizationDisplay;
}

export default function New({
  submitted = false,
  email,
  organization_slug,
  organization,
}: Props) {
  const { data, setData, post, processing } = useForm({
    email_address: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    post(bizminer_app_password_resets_path(organization_slug));
  };

  const signInPath = new_bizminer_app_session_path(organization_slug);

  return (
    <AuthCard
      organization={organization}
      subtitle={submitted ? "Check your email" : "Reset your password"}
    >
      {submitted ? (
        <SuccessMessage email={email} />
      ) : (
        <ResetForm
          onSubmit={handleSubmit}
          email={data.email_address}
          onEmailChange={(value) => setData("email_address", value)}
          processing={processing}
        />
      )}

      <div className="text-center text-sm">
        <Link
          href={signInPath}
          className="text-muted-foreground underline underline-offset-4 hover:text-primary"
        >
          Back to sign in
        </Link>
      </div>
    </AuthCard>
  );
}
